import { create } from "zustand";
import { BookingSettings, WeeklySchedule, PackageConfig, DaySchedule } from "../types";

interface BookingSettingsState {
  settings: BookingSettings | null;
  loading: boolean;
  error: string | null;

  // Actions
  setSettings: (settings: BookingSettings) => void;
  updateWeeklySchedule: (schedule: WeeklySchedule) => void;
  updateDaySchedule: (day: keyof WeeklySchedule, daySchedule: DaySchedule) => void;
  updatePackages: (packages: PackageConfig[]) => void;
  addPackage: (packageConfig: PackageConfig) => void;
  updatePackage: (id: string, updates: Partial<PackageConfig>) => void;
  removePackage: (id: string) => void;
  setBookingWindowDays: (days: number) => void;
  setSlotDuration: (minutes: number) => void;
  setTravelBufferMinutes: (minutes: number) => void;
  setTaxRate: (rate: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const defaultWeeklySchedule: WeeklySchedule = {
  sunday: { enabled: false, timeSlots: [] },
  monday: {
    enabled: true,
    timeSlots: [
      { startTime: "09:00", endTime: "12:00" },
      { startTime: "13:00", endTime: "17:00" },
    ],
  },
  tuesday: {
    enabled: true,
    timeSlots: [
      { startTime: "09:00", endTime: "12:00" },
      { startTime: "13:00", endTime: "17:00" },
    ],
  },
  wednesday: {
    enabled: true,
    timeSlots: [
      { startTime: "09:00", endTime: "12:00" },
      { startTime: "13:00", endTime: "17:00" },
    ],
  },
  thursday: {
    enabled: true,
    timeSlots: [
      { startTime: "09:00", endTime: "12:00" },
      { startTime: "13:00", endTime: "17:00" },
    ],
  },
  friday: {
    enabled: true,
    timeSlots: [
      { startTime: "09:00", endTime: "12:00" },
      { startTime: "13:00", endTime: "17:00" },
    ],
  },
  saturday: { enabled: false, timeSlots: [] },
};

const defaultSettings: BookingSettings = {
  id: "default",
  weeklySchedule: defaultWeeklySchedule,
  packages: [],
  bookingWindowDays: 30,
  slotDuration: 60,
  travelBufferMinutes: 30,
  taxRate: 0,
  updatedAt: new Date(),
  updatedBy: "system",
};

export const useBookingSettingsStore = create<BookingSettingsState>((set) => ({
  settings: defaultSettings,
  loading: false,
  error: null,

  setSettings: (settings) => set({ settings, error: null }),

  updateWeeklySchedule: (schedule) =>
    set((state) => ({
      settings: state.settings
        ? { ...state.settings, weeklySchedule: schedule, updatedAt: new Date() }
        : null,
    })),

  updateDaySchedule: (day, daySchedule) =>
    set((state) => ({
      settings: state.settings
        ? {
            ...state.settings,
            weeklySchedule: {
              ...state.settings.weeklySchedule,
              [day]: daySchedule,
            },
            updatedAt: new Date(),
          }
        : null,
    })),

  updatePackages: (packages) =>
    set((state) => ({
      settings: state.settings ? { ...state.settings, packages, updatedAt: new Date() } : null,
    })),

  addPackage: (packageConfig) =>
    set((state) => ({
      settings: state.settings
        ? {
            ...state.settings,
            packages: [...state.settings.packages, packageConfig],
            updatedAt: new Date(),
          }
        : null,
    })),

  updatePackage: (id, updates) =>
    set((state) => ({
      settings: state.settings
        ? {
            ...state.settings,
            packages: state.settings.packages.map((pkg) =>
              pkg.id === id ? { ...pkg, ...updates } : pkg
            ),
            updatedAt: new Date(),
          }
        : null,
    })),

  removePackage: (id) =>
    set((state) => ({
      settings: state.settings
        ? {
            ...state.settings,
            packages: state.settings.packages.filter((pkg) => pkg.id !== id),
            updatedAt: new Date(),
          }
        : null,
    })),

  setBookingWindowDays: (days) =>
    set((state) => ({
      settings: state.settings
        ? { ...state.settings, bookingWindowDays: days, updatedAt: new Date() }
        : null,
    })),

  setSlotDuration: (minutes) =>
    set((state) => ({
      settings: state.settings
        ? { ...state.settings, slotDuration: minutes, updatedAt: new Date() }
        : null,
    })),

  setTravelBufferMinutes: (minutes) =>
    set((state) => ({
      settings: state.settings
        ? { ...state.settings, travelBufferMinutes: minutes, updatedAt: new Date() }
        : null,
    })),

  setTaxRate: (rate) =>
    set((state) => ({
      settings: state.settings ? { ...state.settings, taxRate: rate, updatedAt: new Date() } : null,
    })),

  setLoading: (loading) => set({ loading }),

  setError: (error) => set({ error }),

  reset: () => set({ settings: defaultSettings, loading: false, error: null }),
}));
